import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import BubbleCanvas from "../components/games/bubble-pop/BubbleCanvas";
import BubbleGameOverModal from "../components/games/bubble-pop/BubbleGameOverModal";
import BackToGamesButton from "../components/games/BackToGamesButton";
import ChallengeBanner, { readChallengeTarget } from "../components/games/ChallengeBanner";
import { useBubbleSoundManager } from "../components/games/bubble-pop/SoundManager";
import {
  BUBBLE_HUES,
  BUBBLE_KINDS,
  COMBO_WINDOW_SECONDS,
  FEVER_DURATION_SECONDS,
  FEVER_EVERY,
  GOLD_RUSH_DURATION_SECONDS,
  GOLD_RUSH_EVERY_SECONDS,
  HUE_GRACE_SECONDS,
  HUE_SWITCH_MIN_SECONDS,
  HUE_WAVE_DURATION_SECONDS,
  MAX_LIVES,
  NORMAL_SCREEN_CAP,
  REACTION_CEILING,
  REACTION_SAMPLE_COUNT,
  REWARD_SCREEN_CAP,
  SUPER_CHARGE_PER_POP,
  activeDecoyCount,
  advanceBubbles,
  buildBubbleSpec,
  bubblePopResultTitle,
  comboMilestone,
  comboMultiplier,
  difficultyRamp,
  doubleSpawnChance,
  hitTestBubble,
  hueWaveFrontY,
  intensityForElapsed,
  nextHueSwitchDelay,
  pickBubbleKind,
  pointsForBubble,
  spawnIntervalForState,
  targetHueBias,
} from "../components/games/bubble-pop/gameUtils";

const F = { fontFamily: "'Fredoka', sans-serif" };
const MISS_RED = "#E63333";
const RAINBOW_CYAN = "#21B3FF";
const MEMORY_PURPLE = "#7A5CF0";

const hexAlpha = (hex, alpha) =>
  `${hex}${Math.round(Math.min(1, Math.max(0, alpha)) * 255)
    .toString(16)
    .padStart(2, "0")}`;

// The WHOLE screen wears the magic colour; the wash strengthens slightly as
// the game speeds up (intensity 0→1), matching the iOS BubblePopBackground.
const hueWash = (hue, intensity) =>
  `linear-gradient(180deg, ${hexAlpha(hue.base, 0.2 + 0.1 * intensity)} 0%, ${hexAlpha(
    hue.base,
    0.38 + 0.14 * intensity
  )} 100%), linear-gradient(#FFFFFF, #FFFFFF)`;

const shuffledHues = () => {
  const copy = [...BUBBLE_HUES];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const StarsChip = ({ score }) => (
  <div
    className="flex h-11 items-center gap-2 rounded-[16px] bg-white/95 px-2.5 sm:h-12 sm:min-w-[104px] sm:px-3"
    style={{
      ...F,
      color: "#1A3764",
      border: "1.5px solid rgba(255,255,255,0.96)",
      boxShadow: "0 3px 0 rgba(21,83,145,0.08), 0 6px 14px rgba(11,26,51,0.1)",
    }}
    aria-label={`${score} points`}
  >
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] bg-[#FFF5CD] sm:h-8 sm:w-8">
      <img src="/assets/pixley_stars.png" alt="" className="h-5 w-5 object-contain sm:h-[22px] sm:w-[22px]" />
    </span>
    <span className="flex min-w-0 flex-col leading-none">
      <span className="hidden text-[9px] font-bold uppercase tracking-[0.12em] text-[#6A7D98] sm:block">
        Points
      </span>
      <span className="mt-0.5 truncate text-[17px] font-bold tabular-nums sm:text-lg">{score}</span>
    </span>
  </div>
);

const LivesChip = ({ lives }) => (
  <div
    className="flex h-11 items-center gap-1 rounded-full bg-white px-3.5 sm:h-12"
    style={{ boxShadow: "0 3px 8px rgba(11,26,51,0.1)" }}
    aria-label={`${lives} of ${MAX_LIVES} lives remaining`}
  >
    {Array.from({ length: MAX_LIVES }).map((_, index) => (
      <span
        key={`heart-${index}`}
        className="text-[20px] leading-none transition-all duration-300 sm:text-[22px]"
        style={{
          color: index < lives ? "#FA596B" : "#C6CBD6",
          transform: index < lives ? "scale(1)" : "scale(0.85)",
        }}
      >
        {index < lives ? "♥" : "♡"}
      </span>
    ))}
  </div>
);

// Always-visible pill showing the current magic colour.
const TargetHueBanner = ({ hue, bumpKey }) => (
  <div
    key={bumpKey}
    className="pointer-events-none flex items-center gap-2 rounded-full px-5 py-2 bubble-hue-pill"
    style={{
      ...F,
      background: `linear-gradient(135deg, ${hue.base}, ${hue.deep})`,
      border: "1.5px solid rgba(255,255,255,0.55)",
      boxShadow: `0 4px 14px ${hue.deep}73`,
    }}
  >
    <span className="text-[13px] font-bold tracking-[0.12em] text-white/85">POP</span>
    <span
      aria-hidden
      className="h-5 w-5 rounded-full"
      style={{
        background: `radial-gradient(circle at 32% 28%, #FFFFFF, ${hue.base})`,
        border: "1.5px solid rgba(255,255,255,0.9)",
      }}
    />
    <span className="text-[18px] font-bold tracking-[0.05em] text-white">{hue.label}</span>
  </div>
);

// The whole-screen spectacle when the magic colour changes: a liquid sheet of
// the NEW colour sweeps down (in time with the model clearing the field) while
// a big bubble + "POP PINK!" banner springs in at the center.
const HueChangeFlash = ({ hue }) => (
  <div className="pointer-events-none absolute inset-0 z-[60] overflow-hidden">
    <div
      className="absolute inset-0 bubble-hue-veil"
      style={{ background: `linear-gradient(135deg, ${hue.base}73, ${hue.deep}59)` }}
    />
    <div className="absolute inset-x-0 bubble-hue-sweep" style={{ height: 260 }}>
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(180deg, ${hue.base}00, ${hue.base}59 60%, ${hue.base}99)` }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-[5px] rounded-full bg-white/95"
        style={{ boxShadow: `0 0 8px rgba(255,255,255,0.9), 0 4px 16px ${hue.base}E6` }}
      />
    </div>
    <div className="absolute inset-0 flex items-center justify-center">
      <span
        className="absolute h-[300px] w-[300px] rounded-full border-4 border-white/85 bubble-hue-ring"
        aria-hidden
      />
      <div className="flex flex-col items-center gap-3.5 bubble-hue-banner">
        <span
          aria-hidden
          className="h-[84px] w-[84px] rounded-full"
          style={{
            background: `radial-gradient(circle at 32% 28%, #FFFFFF, ${hue.base})`,
            border: "3px solid rgba(255,255,255,0.95)",
            boxShadow: `0 6px 14px ${hue.deep}80`,
          }}
        />
        <span
          className="rounded-full px-7 py-3 text-[34px] font-bold leading-none text-white"
          style={{
            ...F,
            letterSpacing: "0.03em",
            background: `linear-gradient(135deg, ${hue.base}, ${hue.deep})`,
            border: "2px solid rgba(255,255,255,0.75)",
            boxShadow: `0 5px 12px ${hue.deep}8C`,
          }}
        >
          POP {hue.label}!
        </span>
      </div>
    </div>
  </div>
);

const GlassBubble = ({ size, base, deep, className = "", children }) => (
  <span
    aria-hidden
    className={`relative inline-flex items-center justify-center rounded-full ${className}`}
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle at 32% 28%, #FFFFFF 0%, ${base} 85%)`,
      border: "3px solid rgba(255,255,255,0.94)",
      boxShadow: `0 7px 16px ${deep}59`,
    }}
  >
    {children}
    <span
      className="absolute rounded-full bg-white/60"
      style={{
        width: size * 0.28,
        height: size * 0.17,
        left: size * 0.16,
        top: size * 0.12,
        transform: "rotate(-30deg)",
        filter: "blur(1px)",
      }}
    />
  </span>
);

const LegendBubble = ({ kind, size = 58 }) => (
  <span
    aria-hidden
    className="relative inline-flex shrink-0 items-center justify-center rounded-full"
    style={{
      width: size,
      height: size,
      ...(kind === "rainbow"
        ? {
            background:
              "conic-gradient(from 30deg, #FF6B6B, #FF9E33, #FFD84D, #45CC73, #21B3FF, #408FFF, #9E6BF5, #FF6B6B)",
            border: "3px solid rgba(255,255,255,0.9)",
            boxShadow: "0 5px 14px rgba(122,92,240,0.4)",
          }
        : {
            background: "radial-gradient(circle at 34% 30%, #FF6B61 0%, #ED3E3E 52%, #DB2929 100%)",
            border: "3px solid rgba(255,255,255,0.85)",
            boxShadow: "0 5px 12px rgba(204,26,26,0.35)",
          }),
    }}
  >
    <span className="font-bold text-white" style={{ ...F, fontSize: Math.round(size * 0.38) }}>
      {kind === "rainbow" ? "✦" : "✕"}
    </span>
  </span>
);

const INTRO_RISERS = [
  { left: "8%", size: 22, delay: 0, duration: 4.4 },
  { left: "20%", size: 12, delay: 1.6, duration: 3.3 },
  { left: "38%", size: 16, delay: 0.8, duration: 3.8 },
  { left: "66%", size: 13, delay: 2.1, duration: 3.5 },
  { left: "82%", size: 26, delay: 0.4, duration: 4.7 },
  { left: "93%", size: 11, delay: 1.2, duration: 3.1 },
];

const INTRO_STEPS = [
  { hue: BUBBLE_HUES[0], text: "Find the magic color in the top bar." },
  { hue: BUBBLE_HUES[1], text: "Pop only that color — others cost a heart." },
  { hue: BUBBLE_HUES[2], text: "Don't let the magic color float away!" },
];

const HowToPlayModal = ({ onStart }) => (
  <div
    className="bp-intro-scrim fixed inset-0 z-[900] flex items-center justify-center px-5 py-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="bubble-pop-how-to-title"
  >
    <div className="bp-intro-card max-h-full w-full max-w-[500px] overflow-y-auto rounded-[34px] bg-white">
      {/* Header band: a little slice of the game — glass bubbles bobbing over
          the blue with tiny ones rising behind them. */}
      <div
        className="relative h-[122px] shrink-0 overflow-hidden"
        style={{ background: "linear-gradient(140deg, #37C2FF 0%, #0077F2 100%)" }}
      >
        {INTRO_RISERS.map((riser, index) => (
          <span
            key={index}
            aria-hidden
            className="bp-intro-rise absolute rounded-full"
            style={{
              left: riser.left,
              width: riser.size,
              height: riser.size,
              background: "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85), rgba(255,255,255,0.14))",
              animationDelay: `${riser.delay}s`,
              animationDuration: `${riser.duration}s`,
            }}
          />
        ))}
        <div className="absolute inset-x-0 bottom-[-12px] flex items-end justify-center gap-4">
          <span className="bp-hero-bob" style={{ animationDelay: "0.3s" }}>
            <GlassBubble size={54} base={BUBBLE_HUES[1].base} deep={BUBBLE_HUES[1].deep} />
          </span>
          <span className="bp-hero-bob mb-2.5" style={{ animationDelay: "0s" }}>
            <GlassBubble size={78} base={BUBBLE_HUES[0].base} deep={BUBBLE_HUES[0].deep}>
              <span className="text-[26px] font-bold text-white/95" style={F}>✦</span>
            </GlassBubble>
          </span>
          <span className="bp-hero-bob" style={{ animationDelay: "0.6s" }}>
            <GlassBubble size={50} base={BUBBLE_HUES[2].base} deep={BUBBLE_HUES[2].deep} />
          </span>
        </div>
      </div>

      <div className="px-6 pb-6 pt-4 text-center sm:px-7">
        <p className="bp-intro-item text-[13px] font-bold tracking-[0.2em]" style={{ ...F, color: "#0077F2", animationDelay: "160ms" }}>
          POP THE MAGIC COLOR
        </p>
        <h2
          id="bubble-pop-how-to-title"
          className="bp-intro-item mt-0.5 text-[32px] leading-tight sm:text-[36px]"
          style={{ ...F, color: "#1B2A55", fontWeight: 700, animationDelay: "220ms" }}
        >
          Bubble Pop
        </h2>

        <div className="mt-4 space-y-2.5 text-left">
          {INTRO_STEPS.map((step, index) => (
            <div
              key={step.text}
              className="bp-intro-item flex items-center gap-3.5 rounded-[20px] px-4 py-3"
              style={{
                background: "#F5F9FF",
                border: "2px solid #E8F0FB",
                animationDelay: `${300 + index * 100}ms`,
              }}
            >
              <span
                className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full text-[21px] text-white"
                style={{
                  ...F,
                  fontWeight: 700,
                  background: `radial-gradient(circle at 32% 28%, ${step.hue.base}, ${step.hue.deep})`,
                  border: "2.5px solid rgba(255,255,255,0.9)",
                  boxShadow: `0 4px 10px ${step.hue.deep}4D`,
                }}
              >
                {index + 1}
              </span>
              <span className="text-[18px] leading-snug sm:text-[19px]" style={{ ...F, color: "#2A4470", fontWeight: 650 }}>
                {step.text}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-2.5 grid grid-cols-2 gap-2.5">
          {[
            ["rainbow", "Pops lots!"],
            ["bomb", "Don't tap!"],
          ].map(([kind, label], index) => (
            <div
              key={kind}
              className="bp-intro-item flex items-center justify-center gap-3 rounded-[20px] px-3 py-3"
              style={{
                background: "#F8FBFF",
                border: "2px solid #E8F0FB",
                animationDelay: `${620 + index * 100}ms`,
              }}
            >
              <span className={kind === "rainbow" ? "bp-legend-float" : "bp-legend-shake"}>
                <LegendBubble kind={kind} size={46} />
              </span>
              <span className="text-[18px] leading-snug sm:text-[19px]" style={{ ...F, color: "#2A4470", fontWeight: 650 }}>
                {label}
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onStart}
          autoFocus
          className="bp-intro-cta mt-4 h-[60px] w-full rounded-full text-[20px] font-bold text-white transition hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
          style={{
            ...F,
            background: "linear-gradient(135deg, #21B3FF, #007AEF)",
            boxShadow: "0 5px 0 #0563BC, 0 12px 22px rgba(0,122,239,0.35)",
          }}
        >
          Let&apos;s pop!&nbsp;&nbsp;<span className="bp-cta-arrow inline-block">→</span>
        </button>
      </div>
    </div>
  </div>
);

const createEngine = (area = { width: 0, height: 0 }) => ({
  area,
  bubbles: [],
  running: false,
  elapsed: 0,
  frameAccumulator: 0,
  lastFrame: 0,
  spawnAccumulator: 0,
  lastPopElapsed: -10,
  combo: 0,
  score: 0,
  lives: MAX_LIVES,
  bestCombo: 0,
  superCharge: 0,
  isFever: false,
  feverEndsAt: 0,
  isGoldRush: false,
  goldRushEndsAt: 0,
  nextGoldRushAt: GOLD_RUSH_EVERY_SECONDS,
  targetHue: BUBBLE_HUES[0],
  decoyOrder: BUBBLE_HUES,
  nextHueSwitchAt: HUE_SWITCH_MIN_SECONDS,
  hueWaveProgress: null,
  previousHue: null,
  hueSwitchedAt: -10,
  reactionSamples: [],
});

// The endless colour-chase survival popper, ported 1:1 from the iOS
// BubblePopGameView: pop the magic colour, avoid the ✕, don't let the magic
// colour escape. Fever, bubble rush, super pop and the colour-change wave all
// behave exactly as they do on iOS.
const ColorBubblePopPage = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const { play } = useBubbleSoundManager();

  const [status, setStatus] = useState("intro"); // intro | playing | ended
  // A friend's beat-my-score link (?challenge=N) — read once on mount.
  const [challengeTarget] = useState(readChallengeTarget);
  const [bubbles, setBubbles] = useState([]);
  const [effects, setEffects] = useState([]);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [lives, setLives] = useState(MAX_LIVES);
  const [intensity, setIntensity] = useState(0);
  const [targetHue, setTargetHue] = useState(BUBBLE_HUES[0]);
  const [hueChangeSignal, setHueChangeSignal] = useState(0);
  const [showHueFlash, setShowHueFlash] = useState(false);
  const [isFever, setIsFever] = useState(false);
  const [isGoldRush, setIsGoldRush] = useState(false);
  const [showRushBanner, setShowRushBanner] = useState(false);
  const [superCharge, setSuperCharge] = useState(0);
  const [comboBanner, setComboBanner] = useState(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [celebrationSignal, setCelebrationSignal] = useState(0);
  const [resultReason, setResultReason] = useState(null);
  const [bestCombo, setBestCombo] = useState(0);
  const [survivedSeconds, setSurvivedSeconds] = useState(0);
  const [starsAwarded, setStarsAwarded] = useState(0);

  const engineRef = useRef(createEngine());
  const bubbleIdRef = useRef(0);
  const effectIdRef = useRef(0);
  const bannerIdRef = useRef(0);
  const effectTimeoutsRef = useRef(new Set());
  const bannerTimeoutRef = useRef(null);
  const hueFlashTimeoutRef = useRef(null);
  const rushBannerTimeoutRef = useRef(null);
  const hasSavedResultRef = useRef(false);

  const clearEffects = useCallback(() => {
    effectTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    effectTimeoutsRef.current.clear();
    setEffects([]);
  }, []);

  const addEffect = useCallback(({ x, y, size = 70 }, text, color, big = false) => {
    effectIdRef.current += 1;
    const effect = { id: `pop-fx-${effectIdRef.current}`, x, y, size, text, color, big };
    setEffects((current) => [...current, effect]);
    const timeoutId = window.setTimeout(() => {
      effectTimeoutsRef.current.delete(timeoutId);
      setEffects((current) => current.filter((item) => item.id !== effect.id));
    }, 760);
    effectTimeoutsRef.current.add(timeoutId);
  }, []);

  const showBanner = useCallback((text, celebrate = false) => {
    if (bannerTimeoutRef.current) window.clearTimeout(bannerTimeoutRef.current);
    bannerIdRef.current += 1;
    setComboBanner({ key: bannerIdRef.current, text });
    if (celebrate) setCelebrationSignal((signal) => signal + 1);
    bannerTimeoutRef.current = window.setTimeout(() => {
      setComboBanner(null);
      bannerTimeoutRef.current = null;
    }, 900);
  }, []);

  const announceHue = useCallback((hue) => {
    setTargetHue(hue);
    setHueChangeSignal((signal) => signal + 1);
    setShowHueFlash(true);
    if (hueFlashTimeoutRef.current) window.clearTimeout(hueFlashTimeoutRef.current);
    hueFlashTimeoutRef.current = window.setTimeout(() => {
      setShowHueFlash(false);
      hueFlashTimeoutRef.current = null;
    }, 1600);
  }, []);

  useEffect(
    () => () => {
      engineRef.current.running = false;
      effectTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      if (bannerTimeoutRef.current) window.clearTimeout(bannerTimeoutRef.current);
      if (hueFlashTimeoutRef.current) window.clearTimeout(hueFlashTimeoutRef.current);
      if (rushBannerTimeoutRef.current) window.clearTimeout(rushBannerTimeoutRef.current);
    },
    []
  );

  const persistResultOnce = useCallback(
    async (finalScore) => {
      if (hasSavedResultRef.current || !apiClient) return;
      hasSavedResultRef.current = true;
      try {
        const response = await apiClient.postGameProgress("BUBBLE_POP", finalScore, true, null);
        if (response?.status || response?.success) {
          setStarsAwarded(Number(response?.stars_awarded || 0));
          window.dispatchEvent(new CustomEvent("games:progress-updated"));
          refreshUserData?.();
        }
      } catch {
        // Finishing the game should never be blocked by a progress-save failure.
      }
    },
    [apiClient, refreshUserData]
  );

  const finishGame = useCallback(
    (reason) => {
      const engine = engineRef.current;
      if (!engine.running) return;
      engine.running = false;
      engine.bubbles = [];
      engine.combo = 0;
      engine.isFever = false;
      engine.isGoldRush = false;
      setBubbles([]);
      setCombo(0);
      setIsFever(false);
      setIsGoldRush(false);
      setShowRushBanner(false);
      setSurvivedSeconds(Math.floor(engine.elapsed));
      setBestCombo(engine.bestCombo);
      setResultReason(reason);
      setStatus("ended");
      play("gameover");
      persistResultOnce(engine.score);
    },
    [persistResultOnce, play]
  );

  const recordReaction = useCallback((seconds) => {
    const engine = engineRef.current;
    engine.reactionSamples.push(Math.min(Math.max(seconds, 0), REACTION_CEILING));
    if (engine.reactionSamples.length > REACTION_SAMPLE_COUNT) engine.reactionSamples.shift();
  }, []);

  const activeDecoys = useCallback(() => {
    const engine = engineRef.current;
    const ramp = difficultyRamp(engine.elapsed, engine.reactionSamples);
    const count = activeDecoyCount(ramp);
    return engine.decoyOrder.filter((hue) => hue.name !== engine.targetHue.name).slice(0, count);
  }, []);

  const spawnBubble = useCallback(() => {
    const engine = engineRef.current;
    // Fairness cap: the screen never holds more bubbles than a kid can
    // physically keep up with; reward windows get more headroom.
    const maxOnScreen = engine.isFever || engine.isGoldRush ? REWARD_SCREEN_CAP : NORMAL_SCREEN_CAP;
    if (engine.bubbles.length >= maxOnScreen) return;

    const ramp = difficultyRamp(engine.elapsed, engine.reactionSamples);
    const kind = pickBubbleKind({ ramp, isFever: engine.isFever, isGoldRush: engine.isGoldRush });
    const bias = targetHueBias({ ramp, isFever: engine.isFever, isGoldRush: engine.isGoldRush });
    const decoys = activeDecoys();
    const hue =
      Math.random() < bias || !decoys.length
        ? engine.targetHue
        : decoys[Math.floor(Math.random() * decoys.length)];

    bubbleIdRef.current += 1;
    engine.bubbles = [
      ...engine.bubbles,
      buildBubbleSpec({
        id: `bubble-${bubbleIdRef.current}`,
        kind,
        hue,
        area: engine.area,
        ramp,
        forgiving: engine.isFever || engine.isGoldRush,
      }),
    ];
  }, [activeDecoys]);

  const startFever = useCallback(() => {
    const engine = engineRef.current;
    engine.isFever = true;
    engine.feverEndsAt = engine.elapsed + FEVER_DURATION_SECONDS;
    setIsFever(true);
    setResetSignal((signal) => signal + 1);
    showBanner("FEVER! ×2 🔥", true);
    play("combo");
  }, [play, showBanner]);

  // Survival-milestone reward: a hazard-free flood of the magic colour.
  const startGoldRush = useCallback(() => {
    const engine = engineRef.current;
    engine.nextGoldRushAt += GOLD_RUSH_EVERY_SECONDS;
    engine.isGoldRush = true;
    engine.goldRushEndsAt = engine.elapsed + GOLD_RUSH_DURATION_SECONDS;
    setIsGoldRush(true);
    setResetSignal((signal) => signal + 1);
    showBanner("BUBBLE RUSH! ✨", true);
    play("combo");
    // Reveal the persistent banner only once the announce flash has faded.
    if (rushBannerTimeoutRef.current) window.clearTimeout(rushBannerTimeoutRef.current);
    rushBannerTimeoutRef.current = window.setTimeout(() => {
      if (engineRef.current.isGoldRush) setShowRushBanner(true);
      rushBannerTimeoutRef.current = null;
    }, 950);
  }, [play, showBanner]);

  // The colour switch: the NEW colour washes down the screen as a sweeping wave
  // that clears EVERY bubble (soft poofs, no points, no penalties).
  const switchHue = useCallback(() => {
    const engine = engineRef.current;
    const ramp = difficultyRamp(engine.elapsed, engine.reactionSamples);
    engine.nextHueSwitchAt = engine.elapsed + nextHueSwitchDelay(ramp);
    const candidates = BUBBLE_HUES.filter((hue) => hue.name !== engine.targetHue.name);
    const newHue = candidates[Math.floor(Math.random() * candidates.length)];

    // Start the sweep; tick() pops bubbles as the wave front passes them.
    // Everything on screen is forgiven so nothing can cost a life mid-wave.
    engine.hueWaveProgress = 0;
    engine.bubbles = engine.bubbles.map((bubble) => ({ ...bubble, forgiving: true }));

    // Open the grace window on the colour we're leaving.
    engine.previousHue = engine.targetHue;
    engine.hueSwitchedAt = engine.elapsed;

    engine.targetHue = newHue;
    announceHue(newHue);
    play("combo");
  }, [announceHue, play]);

  const registerMiss = useCallback(() => {
    const engine = engineRef.current;
    engine.combo = 0;
    // A magic-colour bubble crossing the whole screen unpopped is the clearest
    // "couldn't react in time" signal — feed it in as a worst-case sample.
    recordReaction(REACTION_CEILING);
    // One heart per miss EVENT — a cluster slipping past together should never
    // wipe several lives in a single instant.
    engine.lives = Math.max(0, engine.lives - 1);
    setCombo(0);
    setLives(engine.lives);
    setResetSignal((signal) => signal + 1);
    play("wrong");
    if (engine.lives <= 0) {
      finishGame("missed");
      return;
    }

    // Recovery: sweep away every bubble already in the TOP HALF so a whole
    // cluster can't chain-escape in the next moment.
    const midline = engine.area.height * 0.5;
    engine.bubbles.forEach((bubble) => {
      if (bubble.y < midline) addEffect(bubble, null, "rgba(255,255,255,0.9)");
    });
    engine.bubbles = engine.bubbles.filter((bubble) => bubble.y >= midline);
    setBubbles([...engine.bubbles]);
  }, [addEffect, finishGame, play, recordReaction]);

  const maybeMilestone = useCallback(() => {
    const engine = engineRef.current;
    const word = comboMilestone(engine.combo);
    if (!word) return;
    showBanner(word, true);
    setResetSignal((signal) => signal + 1);
    play("combo");
  }, [play, showBanner]);

  const popRainbow = useCallback(
    (rainbow) => {
      const engine = engineRef.current;
      addEffect(rainbow, "RAINBOW!", MEMORY_PURPLE, true);
      setResetSignal((signal) => signal + 1);
      setCelebrationSignal((signal) => signal + 1);
      play("combo");

      const mult = comboMultiplier(engine.combo);
      const feverBoost = engine.isFever ? 2 : 1;
      let gained = 0;
      // Clears the whole screen — scores every colour, removes bombs SAFELY.
      engine.bubbles.forEach((bubble) => {
        if (bubble.id === rainbow.id) return;
        if (bubble.kind === BUBBLE_KINDS.NORMAL || bubble.kind === BUBBLE_KINDS.RAINBOW) {
          gained += Math.max(bubble.kind === BUBBLE_KINDS.RAINBOW ? 20 : 10, 10) * mult * feverBoost;
        }
        addEffect(
          bubble,
          null,
          bubble.kind === BUBBLE_KINDS.NORMAL ? bubble.hue.base : RAINBOW_CYAN
        );
      });
      engine.score += gained + 20 * mult * feverBoost;
      engine.bubbles = [];
      setScore(engine.score);
      setBubbles([]);
    },
    [addEffect, play]
  );

  const popBubble = useCallback(
    (bubble) => {
      const engine = engineRef.current;
      if (!engine.running) return;

      // Popping a bomb costs one life AND clears the whole screen.
      if (bubble.kind === BUBBLE_KINDS.BOMB) {
        engine.combo = 0;
        engine.lives = Math.max(0, engine.lives - 1);
        setCombo(0);
        setLives(engine.lives);
        setResetSignal((signal) => signal + 1);
        play("wrong");
        addEffect(bubble, "-1", MISS_RED, true);
        engine.bubbles.forEach((other) => {
          if (other.id !== bubble.id) addEffect(other, null, "rgba(255,255,255,0.9)");
        });
        engine.bubbles = [];
        setBubbles([]);
        if (engine.lives <= 0) finishGame("bomb");
        return;
      }

      // Wrong colour: it pops with a dull burst and costs a life — accuracy is
      // the whole game.
      if (bubble.kind === BUBBLE_KINDS.NORMAL && bubble.hue.name !== engine.targetHue.name) {
        // …unless the colour only just changed and this bubble wears the colour
        // that WAS magic. The tap was committed when the switch landed, so it
        // pops for free: no heart, no combo reset, no points.
        const inGrace = engine.elapsed - engine.hueSwitchedAt <= HUE_GRACE_SECONDS;
        if (inGrace && engine.previousHue && bubble.hue.name === engine.previousHue.name) {
          engine.bubbles = engine.bubbles.filter((item) => item.id !== bubble.id);
          setBubbles([...engine.bubbles]);
          addEffect(bubble, null, bubble.hue.base);
          play("pop");
          return;
        }

        engine.combo = 0;
        engine.lives = Math.max(0, engine.lives - 1);
        engine.bubbles = engine.bubbles.filter((item) => item.id !== bubble.id);
        setCombo(0);
        setLives(engine.lives);
        setBubbles([...engine.bubbles]);
        addEffect(bubble, "-1", MISS_RED, true);
        setResetSignal((signal) => signal + 1);
        play("wrong");
        if (engine.lives <= 0) finishGame("wrongColor");
        return;
      }

      // Build combo on a good pop.
      engine.combo = engine.elapsed - engine.lastPopElapsed <= COMBO_WINDOW_SECONDS ? engine.combo + 1 : 1;
      engine.lastPopElapsed = engine.elapsed;
      engine.bestCombo = Math.max(engine.bestCombo, engine.combo);
      setCombo(engine.combo);
      if (engine.combo % FEVER_EVERY === 0) startFever();

      // Reaction sample: this bubble's age IS the time from it appearing to it
      // being popped. Reward-window pops aren't a fair read and are skipped.
      if (bubble.kind === BUBBLE_KINDS.NORMAL && !engine.isFever && !engine.isGoldRush) {
        recordReaction(bubble.age);
      }

      if (bubble.kind === BUBBLE_KINDS.RAINBOW) {
        engine.bubbles = engine.bubbles.filter((item) => item.id !== bubble.id);
        popRainbow(bubble);
        return;
      }

      const mult = comboMultiplier(engine.combo);
      const points = pointsForBubble({ kind: bubble.kind, combo: engine.combo, isFever: engine.isFever });
      engine.score += points;
      engine.bubbles = engine.bubbles.filter((item) => item.id !== bubble.id);
      engine.superCharge = Math.min(1, engine.superCharge + SUPER_CHARGE_PER_POP);

      setScore(engine.score);
      setBubbles([...engine.bubbles]);
      setSuperCharge(engine.superCharge);
      addEffect(bubble, `+${points}`, bubble.hue.deep, mult >= 3);
      play("pop");
      maybeMilestone();
    },
    [addEffect, finishGame, maybeMilestone, play, popRainbow, recordReaction, startFever]
  );

  const handlePopAt = useCallback(
    (point) => {
      const engine = engineRef.current;
      if (!engine.running) return;
      const id = hitTestBubble(engine.bubbles, point);
      if (!id) return;
      const bubble = engine.bubbles.find((item) => item.id === id);
      if (bubble) popBubble(bubble);
    },
    [popBubble]
  );

  // The earned screen-clear: pops every character for points and sweeps bombs
  // away SAFELY.
  const handleSuperPop = useCallback(() => {
    const engine = engineRef.current;
    if (!engine.running || engine.superCharge < 1 || !engine.bubbles.length) return;
    engine.superCharge = 0;
    setSuperCharge(0);
    showBanner("SUPER POP!", true);
    setResetSignal((signal) => signal + 1);
    play("combo");

    const mult = comboMultiplier(engine.combo);
    const feverBoost = engine.isFever ? 2 : 1;
    let gained = 0;
    engine.bubbles.forEach((bubble) => {
      if (bubble.kind === BUBBLE_KINDS.NORMAL || bubble.kind === BUBBLE_KINDS.RAINBOW) {
        gained += Math.max(bubble.kind === BUBBLE_KINDS.RAINBOW ? 20 : 10, 10) * mult * feverBoost;
      }
      addEffect(bubble, null, bubble.kind === BUBBLE_KINDS.NORMAL ? bubble.hue.base : MEMORY_PURPLE);
    });
    engine.score += gained;
    engine.bubbles = [];
    setScore(engine.score);
    setBubbles([]);
    if (gained > 0) {
      addEffect(
        { x: engine.area.width / 2, y: engine.area.height * 0.35, size: 90 },
        `+${gained}`,
        MEMORY_PURPLE,
        true
      );
    }
  }, [addEffect, play, showBanner]);

  const tick = useCallback(
    (dt) => {
      const engine = engineRef.current;
      if (!engine.running || engine.area.width <= 0 || engine.area.height <= 0) return;

      engine.elapsed += dt;
      setIntensity(intensityForElapsed(engine.elapsed));

      // Combo decays if you stop popping.
      if (engine.combo > 0 && engine.elapsed - engine.lastPopElapsed > COMBO_WINDOW_SECONDS) {
        engine.combo = 0;
        setCombo(0);
      }

      // Fever wears off; gold rush arrives on survival milestones and wears off.
      if (engine.isFever && engine.elapsed >= engine.feverEndsAt) {
        engine.isFever = false;
        setIsFever(false);
      }
      if (engine.isGoldRush && engine.elapsed >= engine.goldRushEndsAt) {
        engine.isGoldRush = false;
        setIsGoldRush(false);
        setShowRushBanner(false);
      }
      if (engine.elapsed >= engine.nextGoldRushAt) startGoldRush();

      // The magic colour changes on its own clock (never mid bubble-rush).
      if (engine.elapsed >= engine.nextHueSwitchAt) {
        if (engine.isGoldRush) {
          engine.nextHueSwitchAt = engine.goldRushEndsAt + 1.5;
        } else {
          switchHue();
        }
      }

      // The colour-change wave: a front sweeping down the screen popping every
      // bubble it passes — the once-per-switch full clear.
      if (engine.hueWaveProgress !== null) {
        engine.hueWaveProgress += dt / HUE_WAVE_DURATION_SECONDS;
        const frontY = hueWaveFrontY(engine.hueWaveProgress, engine.area.height);
        const swept = engine.bubbles.filter((bubble) => bubble.y <= frontY);
        if (swept.length) {
          swept.forEach((bubble) =>
            addEffect(
              bubble,
              null,
              bubble.kind === BUBBLE_KINDS.NORMAL ? bubble.hue.base : "rgba(255,255,255,0.9)"
            )
          );
          engine.bubbles = engine.bubbles.filter((bubble) => bubble.y > frontY);
        }
        if (engine.hueWaveProgress >= 1) engine.hueWaveProgress = null;
      }

      // Spawn — accelerates the longer you survive; floods during rewards.
      // Paused while the colour wave is clearing.
      if (engine.hueWaveProgress === null) {
        const ramp = difficultyRamp(engine.elapsed, engine.reactionSamples);
        const interval = spawnIntervalForState({
          ramp,
          isFever: engine.isFever,
          isGoldRush: engine.isGoldRush,
        });
        engine.spawnAccumulator += dt;
        if (engine.spawnAccumulator >= interval) {
          engine.spawnAccumulator = 0;
          spawnBubble();
          if (Math.random() < doubleSpawnChance(ramp)) spawnBubble();
        }
      }

      // Move; detect magic-colour bubbles that escape past the top.
      const moved = advanceBubbles(engine.bubbles, dt, engine.area, engine.targetHue.name);
      engine.bubbles = moved.bubbles;
      setBubbles([...engine.bubbles]);

      // Reward windows flood the screen with more bubbles than a kid can pop,
      // so escapes during them are always free.
      if (moved.escapedCharacters > 0 && !engine.isFever && !engine.isGoldRush) {
        registerMiss();
      }
    },
    [addEffect, registerMiss, spawnBubble, startGoldRush, switchHue]
  );

  useEffect(() => {
    let animationFrameId;
    const frame = (timestamp) => {
      const engine = engineRef.current;
      if (engine.running) {
        if (!engine.lastFrame) engine.lastFrame = timestamp;
        const frameDt = Math.min(0.05, Math.max(0, (timestamp - engine.lastFrame) / 1000));
        engine.lastFrame = timestamp;
        engine.frameAccumulator += frameDt;
        const fixedStep = 1 / 60;
        while (engine.frameAccumulator >= fixedStep) {
          tick(fixedStep);
          engine.frameAccumulator -= fixedStep;
        }
      } else {
        engine.lastFrame = timestamp;
      }
      animationFrameId = window.requestAnimationFrame(frame);
    };
    animationFrameId = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [tick]);

  const handleAreaChange = useCallback((area) => {
    engineRef.current.area = area;
  }, []);

  const startGame = useCallback(() => {
    const area = engineRef.current.area;
    clearEffects();
    if (bannerTimeoutRef.current) window.clearTimeout(bannerTimeoutRef.current);
    setComboBanner(null);

    const fresh = createEngine(area);
    fresh.running = true;
    fresh.lastFrame = performance.now();
    fresh.targetHue = BUBBLE_HUES[Math.floor(Math.random() * BUBBLE_HUES.length)];
    fresh.decoyOrder = shuffledHues();
    engineRef.current = fresh;
    hasSavedResultRef.current = false;

    setBubbles([]);
    setScore(0);
    setCombo(0);
    setLives(MAX_LIVES);
    setIntensity(0);
    setIsFever(false);
    setIsGoldRush(false);
    setShowRushBanner(false);
    setSuperCharge(0);
    setResultReason(null);
    setBestCombo(0);
    setSurvivedSeconds(0);
    setStarsAwarded(0);
    setStatus("playing");
    // Announce the opening colour with the big flash, exactly like iOS.
    announceHue(fresh.targetHue);
  }, [announceHue, clearEffects]);

  const handleStartGame = useCallback(() => {
    play("click");
    startGame();
  }, [play, startGame]);

  const handleBackToGames = useCallback(() => {
    engineRef.current.running = false;
    play("click");
    navigate("/games");
  }, [navigate, play]);

  return (
    <div
      className="relative min-h-screen px-4 pb-8 pt-28 transition-colors duration-700 sm:px-6"
      style={{
        ...F,
        background: `linear-gradient(180deg, ${targetHue.base}26 0%, ${targetHue.base}4D 100%)`,
      }}
    >
      <ChallengeBanner target={challengeTarget} score={score} ended={status === "ended"} />
      <div className="mx-auto w-full max-w-[1100px]">
        <header
          className="mb-3 grid grid-cols-2 items-center gap-2 rounded-[24px] p-2.5 sm:mb-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-4 sm:p-3"
          style={{
            background: "rgba(255,255,255,0.82)",
            border: "1.5px solid rgba(255,255,255,0.9)",
            boxShadow: `0 5px 18px ${targetHue.deep}26`,
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <div className="col-start-1 row-start-2 justify-self-start sm:row-start-1">
            <BackToGamesButton onClick={handleBackToGames} accent={targetHue.deep} />
          </div>

          <div className="col-span-2 row-start-1 min-w-0 text-center sm:col-span-1 sm:col-start-2">
            <h1
              className="text-[28px] leading-none sm:text-[34px]"
              style={{ color: targetHue.deep, fontWeight: 700, letterSpacing: "-0.025em" }}
            >
              Bubble Pop
            </h1>
            <p className="mt-1.5 text-[13px] sm:text-sm" style={{ color: "#5D7695", fontWeight: 600 }}>
              Pop the magic color • avoid the ✕
            </p>
          </div>

          <div className="col-start-2 row-start-2 flex items-center justify-self-end gap-1.5 sm:col-start-3 sm:row-start-1 sm:gap-2">
            <LivesChip lives={lives} />
            <StarsChip score={score} />
          </div>
        </header>

        <div
          className="relative h-[calc(100dvh-16rem)] min-h-[500px] max-h-[740px] w-full overflow-hidden rounded-[28px] sm:h-[calc(100dvh-13rem)]"
          style={{
            border: "3px solid rgba(255,255,255,0.92)",
            boxShadow: `0 8px 0 ${targetHue.deep}14, 0 20px 38px ${targetHue.deep}29`,
          }}
        >
          <BubbleCanvas
            bubbles={bubbles}
            effects={effects}
            onPop={null}
            onPopAt={handlePopAt}
            onAreaChange={handleAreaChange}
            comboBanner={comboBanner}
            resetSignal={resetSignal}
            celebrationSignal={celebrationSignal}
            isFever={isFever}
            isGoldRush={isGoldRush}
            rushHue={targetHue}
            rushBannerVisible={showRushBanner}
            superCharge={superCharge}
            onSuperPop={handleSuperPop}
            isRunning={status === "playing"}
            background={hueWash(targetHue, intensity)}
          >
            <div className="pointer-events-none absolute inset-x-0 top-3 z-50 flex flex-col items-center gap-2 px-3">
              <TargetHueBanner hue={targetHue} bumpKey={hueChangeSignal} />
              {combo >= 2 && (
                <div
                  key={combo}
                  className="rounded-full px-4 py-1.5 text-sm text-white tabular-nums sm:text-base"
                  style={{
                    fontWeight: 700,
                    background: "linear-gradient(180deg, #FFC729 0%, #FFB31A 100%)",
                    border: "1.5px solid rgba(255,255,255,0.85)",
                    boxShadow: "0 4px 10px rgba(255,179,26,0.35)",
                    transform: `scale(${1 + Math.min(combo, 12) * 0.02})`,
                  }}
                >
                  🔥 ×{comboMultiplier(combo)}
                </div>
              )}
            </div>

            {showHueFlash && <HueChangeFlash key={hueChangeSignal} hue={targetHue} />}
          </BubbleCanvas>

          {status === "intro" && <HowToPlayModal onStart={handleStartGame} />}

          <BubbleGameOverModal
            open={status === "ended"}
            reason={resultReason}
            title={bubblePopResultTitle(resultReason)}
            score={score}
            starsAwarded={starsAwarded}
            survivedSeconds={survivedSeconds}
            bestCombo={bestCombo}
            onRetry={handleStartGame}
            onBack={handleBackToGames}
          />
        </div>
      </div>

      <style>{`
        @keyframes bubbleHuePillBump {
          0% { transform: scale(1); }
          35% { transform: scale(1.12); }
          100% { transform: scale(1); }
        }
        .bubble-hue-pill { animation: bubbleHuePillBump 700ms ease-out; }
        @keyframes bubbleHueVeil {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .bubble-hue-veil { animation: bubbleHueVeil 1100ms ease-out 100ms forwards; }
        @keyframes bubbleHueSweep {
          0% { transform: translateY(-300px); }
          100% { transform: translateY(110vh); }
        }
        .bubble-hue-sweep { animation: bubbleHueSweep 620ms ease-in-out forwards; }
        @keyframes bubbleHueRing {
          0% { transform: scale(0.25); opacity: 0.9; }
          100% { transform: scale(3.4); opacity: 0; }
        }
        .bubble-hue-ring { animation: bubbleHueRing 1050ms ease-out 150ms both; }
        @keyframes bubbleHueBanner {
          0% { transform: scale(0.4) rotate(-6deg); opacity: 0; }
          60% { transform: scale(1.06) rotate(1deg); opacity: 1; }
          80%, 100% { transform: scale(1) rotate(0); opacity: 1; }
        }
        .bubble-hue-banner { animation: bubbleHueBanner 650ms cubic-bezier(.2,.9,.3,1.2) 200ms both; }

        /* --- Intro card --- */
        @keyframes bpIntroScrimIn { from { opacity: 0; } to { opacity: 1; } }
        .bp-intro-scrim {
          background: rgba(13, 36, 68, 0.55);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          animation: bpIntroScrimIn 300ms ease-out;
        }
        @keyframes bpIntroCardIn {
          0% { transform: translateY(36px) scale(0.86) rotate(-1.5deg); opacity: 0; }
          60% { transform: translateY(-6px) scale(1.02) rotate(0.5deg); opacity: 1; }
          100% { transform: translateY(0) scale(1) rotate(0); opacity: 1; }
        }
        .bp-intro-card {
          box-shadow: 0 30px 70px rgba(6, 30, 64, 0.42);
          animation: bpIntroCardIn 540ms cubic-bezier(0.2, 0.9, 0.3, 1.1) both;
        }
        @keyframes bpIntroRise {
          0% { bottom: -30px; opacity: 0; transform: translateX(0); }
          20% { opacity: 0.9; }
          100% { bottom: 108%; opacity: 0; transform: translateX(12px); }
        }
        .bp-intro-rise { bottom: -30px; animation: bpIntroRise linear infinite; }
        @keyframes bpHeroBob {
          from { transform: translateY(-6px); }
          to { transform: translateY(6px); }
        }
        .bp-hero-bob { display: inline-flex; animation: bpHeroBob 1.8s ease-in-out infinite alternate; }
        @keyframes bpIntroItemIn {
          from { transform: translateY(14px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .bp-intro-item { animation: bpIntroItemIn 420ms ease-out both; }
        @keyframes bpLegendFloat {
          from { transform: translateY(-3px) rotate(-4deg); }
          to { transform: translateY(3px) rotate(4deg); }
        }
        .bp-legend-float { display: inline-flex; animation: bpLegendFloat 1.6s ease-in-out infinite alternate; }
        @keyframes bpLegendShake {
          0%, 76%, 100% { transform: rotate(0); }
          82% { transform: rotate(-9deg); }
          88% { transform: rotate(8deg); }
          94% { transform: rotate(-5deg); }
        }
        .bp-legend-shake { display: inline-flex; animation: bpLegendShake 2.6s ease-in-out infinite; }
        @keyframes bpCtaBreath {
          from { transform: scale(1); }
          to { transform: scale(1.02); }
        }
        .bp-intro-cta {
          animation: bpIntroItemIn 420ms ease-out 920ms both, bpCtaBreath 1.15s ease-in-out 1.5s infinite alternate;
        }
        @keyframes bpCtaArrow {
          from { transform: translateX(0); }
          to { transform: translateX(5px); }
        }
        .bp-cta-arrow { animation: bpCtaArrow 0.8s ease-in-out infinite alternate; }
        @media (prefers-reduced-motion: reduce) {
          .bp-intro-card, .bp-intro-rise, .bp-hero-bob, .bp-intro-item, .bp-legend-float,
          .bp-legend-shake, .bp-intro-cta, .bp-cta-arrow { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default ColorBubblePopPage;
